import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Zap } from 'lucide-react';
import { Button } from '../ui/button';

// Brand logos mapping
const brandLogos = {
  "WHES": "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/vziwce9r_logo-WHESS.png",
  "Deye": "https://au.deyeinverter.com/template/eo/images/logo.png",
  "Sigenergy": "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/da5t6cpp_logo-sigenergy.png"
};

const projects = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/ko9mw1nc_image.png",
    location: "Tarneit, VIC",
    systemType: "WHES Battery Storage",
    brand: "WHES"
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/16bahwen_image.png",
    location: "Hampton Park, VIC",
    systemType: "Deye Battery + Inverter",
    brand: "Deye"
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/3auanlb7_image.png",
    location: "Greenvale, VIC",
    systemType: "Sigenergy Battery System",
    brand: "Sigenergy"
  },
  {
    id: 4,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/uyof1mwh_image.png",
    location: "Diggers Rest, VIC",
    systemType: "Deye Battery Stack + Inverter",
    brand: "Deye"
  },
  {
    id: 5,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/f6rz3ksx_image.png",
    location: "Point Cook, VIC",
    systemType: "Deye Battery System",
    brand: "Deye"
  },
  {
    id: 6,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/sa0h28a8_image.png",
    location: "Wyndham Vale, VIC",
    systemType: "Deye Battery Stack + Inverter",
    brand: "Deye"
  }
];

const RecentProjectsSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10">
          <div>
            <span className="text-[#B5D43B] font-semibold text-sm uppercase tracking-wider">Our Work</span>
            <h2 className="heading-2 mt-2">Recent Projects</h2>
            <p className="text-gray-600 mt-2 max-w-xl">
              See how we've helped Australian homeowners achieve energy independence with our complete home electrification solutions.
            </p>
          </div>
          <Link to="/projects" className="mt-4 sm:mt-0">
            <Button className="btn-secondary gap-2">
              View All Projects
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div 
              key={project.id} 
              className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={project.image} 
                  alt={`Solar installation in ${project.location}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                
                {/* Brand Logo Overlay */}
                <div className="absolute top-3 right-3">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg px-2 py-1.5 shadow-lg">
                    <img 
                      src={brandLogos[project.brand]} 
                      alt={project.brand}
                      className="h-6 w-auto max-w-[80px] object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-[#3D5A47]">${project.brand}</span>`;
                      }}
                    />
                  </div>
                </div>
                
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-1.5 text-white">
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium text-sm">{project.location}</span>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-[#3D5A47]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#3D5A47]">{project.systemType}</h3>
                    <p className="text-sm text-gray-500">{project.brand}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecentProjectsSection;
